import { GIFT_REGISTRY_WIDGET_NAMES } from "@/constants/widget-names";
import { urlsBuilderContextToken } from "@/context_tokens";
import GiftRegistryQuery from "@apps/gift_registry/queries/GiftRegistryQuery";
import GiftRegistryService from "@apps/gift_registry/services/GiftRegistryService";
import GiftRegistryStorefrontValuesService from "@apps/gift_registry/services/GiftRegistryStorefrontValuesService";
import CustomizationManager from "@lib/CustomizationManager/CustomizationManager";
import { AuthenticationModule } from "@modules/authentication/AuthenticationModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { I18nModule } from "@modules/i18n/I18nModule";
import { productsQueryToken } from "@modules/products/tokens";
import { SvelteComponent } from "svelte";
import { container } from "tsyringe";

import { LoggerInterface } from "@interfaces/LoggerInterface";
import { WidgetInterface } from "@interfaces/WidgetInterface";

import GiftRegistryPageWidgetComponent from "./GiftRegistryPageWidget.svelte";
import { GiftRegistryPageWidgetConfigInterface } from "./GiftRegistryPageWidgetConfig";

class GiftRegistryPageWidget implements WidgetInterface {
    private component: SvelteComponent | undefined = undefined;

    constructor(
        private config: GiftRegistryPageWidgetConfigInterface,
        private readonly logger: LoggerInterface
    ) {
        this.logger.debug("GiftRegistryPageWidget is initialized", config);
    }

    async init() {
        const giftRegistryService = container.resolve(GiftRegistryService);
        const giftRegistryQuery = container.resolve(GiftRegistryQuery);
        const giftRegistryStorefrontValuesService = container.resolve(
            GiftRegistryStorefrontValuesService
        );
        const productsQuery = container.resolve(productsQueryToken);
        const authenticationInfoProvider =
            container.resolve(AuthenticationModule).exports
                .authenticationInfoProvider;

        const i18nModule = container.resolve(I18nModule);

        i18nModule.exports.i18n.registerLoader(
            "giftRegistry.GiftRegistryPageWidget",
            i18nModule.exports.i18nApiLoaderFactory.getLoader(
                "giftRegistry.GiftRegistryPageWidget"
            )
        );

        i18nModule.exports.i18n.registerLoader(
            "giftRegistry.critical.GiftRegistryPageWidget",
            i18nModule.exports.i18nCriticalLoaderFactory.getLoader(
                "giftRegistry.critical.GiftRegistryPageWidget"
            )
        );

        const element = this.config.target;
        const shadowRoot =
            element.shadowRoot || element.attachShadow({ mode: "open" });
        const customizationManager = container.resolve(CustomizationManager);

        const customizationManagerConfig = {
            widgetName: GIFT_REGISTRY_WIDGET_NAMES.GIFT_REGISTRY_PAGE_WIDGET,
            placeholder: element,
            shadowRoot,
        };

        customizationManager.injectStylesFromCustomization(
            customizationManagerConfig
        );
        await customizationManager.beforeWidgetInject(
            customizationManagerConfig
        );

        const contextMap = new Map();
        contextMap.set(
            urlsBuilderContextToken,
            container.resolve(CommonModule).exports.urlsBuilder
        );

        this.component = new GiftRegistryPageWidgetComponent({
            target: shadowRoot,
            props: {
                giftRegistryService,
                giftRegistryQuery,
                giftRegistryStorefrontValuesService,
                productsQuery,
                authenticationInfoProvider,
                showTitle: this.config.showTitle || false,
                afterWidgetInject: () =>
                    customizationManager.afterWidgetInject(
                        customizationManagerConfig
                    ),
            },
            context: contextMap,
        });

        return this;
    }

    destroy() {
        if (this.component) {
            this.component.$destroy();
        }
    }
}

export default GiftRegistryPageWidget;
